import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { extractCampaignPitchFromFile } from '../../../API/agent';
import { OFFICE_UPLOAD_ACCEPT, OFFICE_UPLOAD_ALLOWED_EXTENSIONS, SUPPORTED_UPLOAD_FORMATS_LABEL } from '../uploadFileConfig';

const useStyles = makeStyles(() => ({
  mainContainer: {
    width: '100%',
    maxWidth: '650px',
    margin: '0 auto 30px auto',
  },
  actions: {
    marginTop: '16px',
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  helper: {
    marginTop: '10px',
  },
}));

const MAX_SIZE_BYTES = 25 * 1024 * 1024;

const CampaignPitchUpload = props => {
  const classes = useStyles();
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const extractionStatus = props.surveyAnswersState?.Post_Pitch_Extraction_Status;
  const requestIdRef = useRef(0);

  const extracted = useMemo(() => extractionStatus === 'completed', [extractionStatus]);

  const validateFile = file => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!OFFICE_UPLOAD_ALLOWED_EXTENSIONS.includes(extension)) return 'Only PDF, DOC, DOCX, PPT, and PPTX files are supported.';
    if (file.size > MAX_SIZE_BYTES) return 'File is too large. Maximum allowed size is 25 MB.';
    return '';
  };

  const updateEditablePitch = pitchText => {
    const currentValues = Array.isArray(props.surveyAnswersState?.Post_Text)
      ? props.surveyAnswersState.Post_Text
      : [
          { id: 'field_1', ai: true, text: 'Suggested marketing campaign pitch:', value: '', isSelected: false },
          {
            id: 'field_2',
            ai: false,
            text: 'Or enter the text of your marketing campaign pitch (if any).',
            value: '',
            isSelected: true,
          },
        ];
    const updated = currentValues.map(item => {
      if (item?.id === 'field_2') {
        return {
          ...item,
          value: pitchText,
          isSelected: true,
        };
      }

      return {
        ...item,
        isSelected: item?.id === 'field_2' ? true : false,
      };
    });

    props.handleSurveyAnswers(updated, 'Post_Text');
  };

  const onUpload = async event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError('');
    props.handleSurveyAnswers('processing', 'Post_Pitch_Extraction_Status');
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    try {
      const result = await extractCampaignPitchFromFile({
        file,
        surveyId: props.surveyAnswersState?.id,
        context: props.surveyAnswersState,
      });

      if (requestIdRef.current !== requestId) {
        return;
      }

      if (result?.err) {
        throw new Error(result?.errMessage || 'Campaign pitch extraction failed.');
      }

      const extractedPitch = result?.result?.pitchSummary || '';
      if (Array.isArray(result?.result?.postText) && result.result.postText.length) {
        props.handleSurveyAnswers(result.result.postText, 'Post_Text');
      } else {
        updateEditablePitch(extractedPitch);
      }
      props.handleSurveyAnswers(result?.result?.pitchFileName || file.name, 'Post_Pitch_File_Name');
      props.handleSurveyAnswers(result?.result?.pitchFileUrl || '', 'Post_Pitch_File_Url');
      props.handleSurveyAnswers('completed', 'Post_Pitch_Extraction_Status');
      props.handleSurveyAnswers(result?.result?.rawResponse || null, 'Post_Pitch_Extraction_Raw_Response');
    } catch (err) {
      if (requestIdRef.current === requestId) {
        setError(err.message || 'Campaign pitch extraction failed.');
        props.handleSurveyAnswers('failed', 'Post_Pitch_Extraction_Status');
      }
    } finally {
      if (requestIdRef.current === requestId) {
        setUploading(false);
      }
      event.target.value = '';
    }
  };

  return (
    <Box className={classes.mainContainer}>
      <Typography variant="h3">Upload campaign pitch document (if you have it).</Typography>
      <Typography variant="body1" className={classes.helper}>
        {SUPPORTED_UPLOAD_FORMATS_LABEL}
      </Typography>
      <Box className={classes.actions}>
        <Button variant="contained" component="label" disabled={uploading} style={{ background: '#19989B', color: '#fff' }}>
          {uploading ? (
            <>
              <CircularProgress size={16} style={{ color: '#fff', marginRight: 8 }} /> Analyzing...
            </>
          ) : (
            'Choose file'
          )}
          <input hidden type="file" accept={OFFICE_UPLOAD_ACCEPT} onChange={onUpload} />
        </Button>
      </Box>
      {extracted ? (
        <Typography style={{ marginTop: 12 }}>
          Campaign pitch generated from the uploaded file. Review and edit the text above before continuing.
        </Typography>
      ) : null}
      {error ? <Typography style={{ marginTop: 12, color: '#D32F2F' }}>{error} Please retry with another file.</Typography> : null}
    </Box>
  );
};

export default CampaignPitchUpload;
